import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
// Empty grouped-deals shape (server now returns the 4 tables pre-grouped).
const EMPTY_DEAL_GROUPS = {
    close_to_close: [],
    newly_added: [],
    closed_since_last_report: [],
    lost_from_prior: [],
};
/* initial state */
const initialState = {
    deals: EMPTY_DEAL_GROUPS,
    loading: false,
    saving: false,
    error: null,
    aeViews: {},
    bdrViews: {},
    reportLoading: false,
    reportError: null,
    reportLastUpdated: null,
    reportSyncing: false,
    legacyDataSaving: false,
};
/* async thunks */
export const fetchDeals = createAsyncThunk("salesReporting/fetchDeals", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/sales-reporting/deals`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            // Server returns the 4 tables pre-grouped; merge over the empty shape
            // so every key is always present even if the API omits one.
            return { ...EMPTY_DEAL_GROUPS, ...(res.data.data || {}) };
        }
        const backendMsg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to fetch deals";
        return rejectWithValue(backendMsg);
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err?.message || "Fetch deals error");
    }
});
// Fetch a report matrix by type (ae | bdr) — DB-only, computed server-side.
export const fetchReport = createAsyncThunk("salesReporting/fetchReport", async ({ type, year }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/sales-reporting/report?type=${encodeURIComponent(type)}&year=${encodeURIComponent(year)}`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            const d = res.data.data || {};
            return {
                type,
                views: (d.views || {}),
                lastUpdated: (d.lastUpdated ?? null),
            };
        }
        const backendMsg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to fetch report";
        return rejectWithValue(backendMsg);
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err?.message || "Fetch report error");
    }
});
// Manual sync — the HubSpot re-pull now runs in the BACKGROUND on the server (so a
// long run can't 504 behind the gateway). We POST to start it, show a "started"
// toast (old data stays on screen, refresh icon spins), then poll /sync/status
// until it finishes, and finally fetch fresh data once + show a "completed" toast.
export const syncReport = createAsyncThunk("salesReporting/syncReport", async ({ year, month, type }, { getState, dispatch, rejectWithValue }) => {
    const token = getState().auth?.token;
    const authHeader = { Authorization: `Bearer ${token}` };
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
        toast.success("Sync started. This may take a few moments.");
        // Kick off the background job — returns immediately with { status }.
        const res = await ApiCall("POST", `/api/sales-reporting/sync`, month ? { year, month } : { year }, authHeader);
        if (!(res && "data" in res && res.data?.success)) {
            const msg = res?.response?.data?.message ||
                res?.data?.message ||
                "Failed to sync sales reporting";
            toast.error(msg);
            return rejectWithValue(msg);
        }
        // Nothing to sync for this period — treat as done.
        if (res.data.data?.status === "skipped") {
            toast.success(res.data.message || "Nothing to sync");
            await dispatch(fetchReport({ type: type || "bdr", year }));
            return;
        }
        // Poll for completion (cap at ~15 min so we never hang forever).
        const POLL_MS = 5000;
        const MAX_MS = 15 * 60 * 1000;
        const startedAt = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
            await sleep(POLL_MS);
            const st = await ApiCall("GET", `/api/sales-reporting/sync/status`, undefined, authHeader);
            const status = st?.data?.data;
            if (status && status.running === false) {
                if (status.error) {
                    toast.error(status.error);
                    return rejectWithValue(status.error);
                }
                break; // finished successfully
            }
            if (Date.now() - startedAt > MAX_MS) {
                const msg = "Sync is taking longer than expected. Please refresh in a bit.";
                toast.error(msg);
                return rejectWithValue(msg);
            }
        }
        toast.success("Sync completed");
        // Fetch fresh data once, after the sync finishes (the other tab refreshes when opened).
        await dispatch(fetchReport({ type: type || "bdr", year }));
        return;
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Sync sales reporting error";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
// Fetch a BDR entity's daily records for a whole month (Legacy Data Edit popup populates its date picker from these).
export const fetchLegacyDataMonth = createAsyncThunk("salesReporting/fetchLegacyDataMonth", async ({ view, month, user_id, name }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const qs = new URLSearchParams({ view, month });
        if (user_id)
            qs.set("user_id", user_id);
        if (name)
            qs.set("name", name);
        const res = await ApiCall("GET", `/api/sales-reporting/kpi?${qs.toString()}`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return (res.data.data?.dates || []);
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to fetch Legacy Data";
        return rejectWithValue(msg);
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err?.message || "Fetch Legacy Data error");
    }
});
// Update one BDR entity's measures for a single (past-month) day, then refresh the BDR matrix.
export const updateLegacyDataCell = createAsyncThunk("salesReporting/updateLegacyDataCell", async ({ payload, year }, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PUT", `/api/sales-reporting/kpi`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Legacy Data updated successfully");
            await dispatch(fetchReport({ type: "bdr", year }));
            return res.data.data;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to update Legacy Data";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Update Legacy Data error";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
// Fetch a BDR rep's current-month drill-down detail (Outreach Companies / Booked / Happened / Enrollments).
export const fetchUserMonthDetails = createAsyncThunk("salesReporting/fetchUserMonthDetails", async ({ user_id, month }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const qs = new URLSearchParams({ user_id, month });
        const res = await ApiCall("GET", `/api/sales-reporting/details?${qs.toString()}`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return res.data.data;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to fetch details";
        return rejectWithValue(msg);
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err?.message || "Fetch details error");
    }
});
export const createDeal = createAsyncThunk("salesReporting/createDeal", async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/sales-reporting/deals`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Deal created successfully");
            dispatch(fetchDeals());
            return res.data.data;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to create deal";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Create deal failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const updateDeal = createAsyncThunk("salesReporting/updateDeal", async ({ id, payload }, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PUT", `/api/sales-reporting/deals/${id}`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Deal updated successfully");
            dispatch(fetchDeals());
            return res.data.data;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to update deal";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Update deal failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const deleteDeal = createAsyncThunk("salesReporting/deleteDeal", async (id, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("DELETE", `/api/sales-reporting/deals/${id}`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Deal deleted successfully");
            dispatch(fetchDeals());
            return id;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to delete deal";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Delete deal failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
/* slice */
const salesReportingSlice = createSlice({
    name: "salesReporting",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDeals.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchDeals.fulfilled, (state, action) => {
            state.loading = false;
            state.deals = action.payload;
        })
            // (rejected handler below resets to the empty grouped shape)
            .addCase(fetchDeals.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "An error occurred";
            state.deals = EMPTY_DEAL_GROUPS;
        })
            .addCase(fetchReport.pending, (state) => {
            state.reportLoading = true;
            state.reportError = null;
        })
            .addCase(fetchReport.fulfilled, (state, action) => {
            state.reportLoading = false;
            if (action.payload.type === "bdr") {
                state.bdrViews = action.payload.views;
            }
            else {
                state.aeViews = action.payload.views;
            }
            // Both tabs share the same sync timestamp; keep the latest non-null.
            if (action.payload.lastUpdated)
                state.reportLastUpdated = action.payload.lastUpdated;
        })
            .addCase(fetchReport.rejected, (state, action) => {
            state.reportLoading = false;
            state.reportError = action.payload || "An error occurred";
        })
            .addCase(syncReport.pending, (state) => {
            state.reportSyncing = true;
        })
            .addCase(syncReport.fulfilled, (state) => {
            state.reportSyncing = false;
        })
            .addCase(syncReport.rejected, (state) => {
            state.reportSyncing = false;
        })
            .addCase(updateLegacyDataCell.pending, (state) => {
            state.legacyDataSaving = true;
        })
            .addCase(updateLegacyDataCell.fulfilled, (state) => {
            state.legacyDataSaving = false;
        })
            .addCase(updateLegacyDataCell.rejected, (state) => {
            state.legacyDataSaving = false;
        })
            .addCase(createDeal.pending, (state) => {
            state.saving = true;
        })
            .addCase(createDeal.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(createDeal.rejected, (state) => {
            state.saving = false;
        })
            .addCase(updateDeal.pending, (state) => {
            state.saving = true;
        })
            .addCase(updateDeal.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(updateDeal.rejected, (state) => {
            state.saving = false;
        })
            .addCase(deleteDeal.pending, (state) => {
            state.saving = true;
        })
            .addCase(deleteDeal.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(deleteDeal.rejected, (state) => {
            state.saving = false;
        });
    },
});
/* selectors */
export const selectDeals = (state) => state.salesReporting.deals;
export const selectDealsLoading = (state) => state.salesReporting.loading;
export const selectDealsSaving = (state) => state.salesReporting.saving;
export const selectAeReportViews = (state) => state.salesReporting.aeViews;
export const selectBdrReportViews = (state) => state.salesReporting.bdrViews;
export const selectReportLoading = (state) => state.salesReporting.reportLoading;
export const selectReportLastUpdated = (state) => state.salesReporting.reportLastUpdated;
export const selectReportSyncing = (state) => state.salesReporting.reportSyncing;
export const selectLegacyDataSaving = (state) => state.salesReporting.legacyDataSaving;
export default salesReportingSlice.reducer;
